import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  Text,
  Flex,
  Tooltip,
  useColorMode,
  IconButton,
  DrawerBody,
  DrawerFooter,
} from '@chakra-ui/react';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { useActiveSession, useSession } from '@texas/hooks/useSession';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { useTranslation } from 'react-i18next';
import { EditUser } from '@texas/components/users/EditUser';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { JobList } from '@texas/components/jobs/JobList';
import { versionStorage } from '@texas/utils/versionStorage';
import { CopyValue } from '@texas/components/shared/CopyValue';

export function ProfileDrawer({ isOpen, onClose }: SharedDisclosureProps) {
  const session = useActiveSession();
  const { logout } = useSession();
  const {
    value,
    onOpen: onOpenEditUser,
    onClose: onCloseEditUser,
    isOpen: isOpenEditUser,
  } = useValueDisclosure<number>();
  const { t } = useTranslation();
  const appVersion = versionStorage.get() ?? 'Unknown';

  return (
    <Drawer size="md" isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          <Flex flexDir="column" pr={4}>
            <Text variant="header-medium">{session.currentUser.name}</Text>
            <Text variant="small" fontSize="sm" noOfLines={1}>
              {session.currentUser.email}
            </Text>
            <Flex gap={2} pt={2}>
              <ThemeController />
              <Tooltip label={t('userProfile.editUserProfile')}>
                <IconButton
                  variant="texas-light"
                  icon={<Icons.Pencil boxSize={defaultIconSize} />}
                  aria-label="Edit profile"
                  onClick={() => {
                    onOpenEditUser(session.currentUser.id);
                  }}
                />
              </Tooltip>
              <Tooltip label={t('actionBar.signOut')}>
                <IconButton
                  variant="texas-light"
                  icon={<Icons.Logout boxSize={defaultIconSize} />}
                  aria-label="Signout"
                  onClick={async () => {
                    await logout();
                  }}
                />
              </Tooltip>
            </Flex>
          </Flex>
        </DrawerHeader>
        <DrawerBody>
          <JobList />
        </DrawerBody>
        <DrawerFooter justifyContent="center" borderTopWidth="1px">
          <CopyValue value={appVersion} label="Version" />
        </DrawerFooter>
      </DrawerContent>
      {value && (
        <EditUser
          id={value}
          onClose={onCloseEditUser}
          isOpen={isOpenEditUser}
        />
      )}
    </Drawer>
  );
}

function ThemeController() {
  const { colorMode, toggleColorMode } = useColorMode();
  const { t } = useTranslation();
  return (
    <Tooltip label={t('theme.toggle')}>
      <IconButton
        variant="theme"
        icon={
          colorMode === 'dark' ? (
            <Icons.WeatherNight boxSize={defaultIconSize} />
          ) : (
            <Icons.WhiteBalanceSunny boxSize={defaultIconSize} />
          )
        }
        aria-label="Theme"
        onClick={toggleColorMode}
      />
    </Tooltip>
  );
}
